import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PeriodPicker } from '@/components/ui/period-picker';
import { DateRange } from 'react-day-picker';
import { Plus, Target, Info, Activity } from 'lucide-react';
import { EmptyTrackingState } from '@/components/tracking/EmptyTrackingState';
import { PlatformSelection } from '@/components/tracking/PlatformSelection';
import { WebhooksTable } from '@/components/tracking/WebhooksTable';
import { MetricsTablePremium } from '@/components/tracking/MetricsTablePremium';
import { WebhookData } from '@/components/tracking/types';
import { useTrackingData } from '@/components/tracking/useTrackingData';
import { useTrackingStore } from '@/store/trackingStore';
import { platformsData, platformSlugToApiName } from '@/components/tracking/platformsData';

type TrackingStep = 'empty' | 'selection' | 'dashboard';

export default function Tracking() {
  useLanguage();
  const [currentStep, setCurrentStep] = useState<TrackingStep>('empty');
  const [activeTab, setActiveTab] = useState<string>('cadastro');
  const [savedWebhooks, setSavedWebhooks] = useState<WebhookData[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const previousWebhookCountRef = useRef<number>(0);
  const { selectedPlatforms, setSelectedPlatforms } = useTrackingStore();

  // Buscar dados de tracking (plataformas e vídeos) ao carregar a página
  const { data: trackingData, isLoading: isLoadingTrackingData } = useTrackingData();

  // Verificar se há integrações existentes ao carregar a página
  useEffect(() => {
    if (trackingData?.integrations && trackingData.integrations.length > 0) {
      setCurrentStep('dashboard');

      // Converter integrações da API para webhooks
      const webhooksFromIntegrations: WebhookData[] = trackingData.integrations.map(integration => {
        // Criar mapeamento reverso: nome da API -> slug do frontend
        const apiNameToSlug = Object.fromEntries(
          Object.entries(platformSlugToApiName).map(([slug, apiName]) => [apiName, slug])
        );

        // Buscar o slug usando o mapeamento reverso
        const platformSlug = apiNameToSlug[integration.platform_name] || integration.platform_name;

        return {
          id: integration.id_integration,
          platform: platformSlug,
          platformId: integration.id_plataform,
          url: integration.webhook_url,
          status: integration.has_first_sail ? 'active' : 'waiting',
          selectedVideoId: integration.id_video || '',
          integrationId: integration.id_integration,
          platformToken: integration.token_plataform || '',
        };
      });

      setSavedWebhooks(webhooksFromIntegrations);

      // Popular o store com as plataformas únicas das integrações
      const apiNameToSlug = Object.fromEntries(
        Object.entries(platformSlugToApiName).map(([slug, apiName]) => [apiName, slug])
      );

      const uniquePlatforms = Array.from(
        new Map(
          trackingData.integrations.map(integration => {
            const platformSlug = apiNameToSlug[integration.platform_name] || integration.platform_name;
            const localPlatform = platformsData.find(p => p.id === platformSlug);

            return [
              integration.platform_name,
              {
                slug: platformSlug,
                platformId: integration.id_plataform,
                name: localPlatform?.name || integration.platform_name,
              }
            ];
          })
        ).values()
      );

      setSelectedPlatforms(uniquePlatforms);
    }
  }, [trackingData?.integrations]);

  // Watch for when all webhooks are deleted and return to empty state
  useEffect(() => {
    const currentCount = savedWebhooks.length;
    const previousCount = previousWebhookCountRef.current;
  }, [savedWebhooks, currentStep]);

  const handleStartTracking = () => {
    setCurrentStep('selection');
  };

  const handleBackToEmpty = () => {
    // Se há integrações existentes, volta para dashboard
    // Se não há, volta para empty
    if (trackingData?.integrations && trackingData.integrations.length > 0) {
      setCurrentStep('dashboard');
    } else {
      setCurrentStep('empty');
    }
  };

  const handlePlatformSelection = () => {
    setCurrentStep('dashboard');
    setActiveTab('cadastro'); // Always go to cadastro tab when adding new platforms
  };

  const handleWebhooksSaved = (webhooks: WebhookData[]) => {
    const previousCount = previousWebhookCountRef.current;
    const currentCount = webhooks.length;

    setSavedWebhooks(webhooks);

    // Update the ref with current count
    previousWebhookCountRef.current = currentCount;

    // If we're in dashboard and all SAVED webhooks were removed, redirect to empty
    if (currentStep === 'dashboard' && currentCount === 0 && previousCount > 0) {
      setTimeout(() => {
        setCurrentStep('empty');
        previousWebhookCountRef.current = 0;
      }, 300);
    }
  };

  const handleBackToSelection = () => {
    // If in metrics tab, go back to cadastro tab first
    if (activeTab === 'metricas') {
      setActiveTab('cadastro');
    } else {
      // If in cadastro tab, go back to platform selection
      setCurrentStep('selection');
    }
  };

  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Premium */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            <div className="flex items-center gap-2.5">
              <Activity className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
              <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
                {translateInterfaceText("Rastreamento")}</h1>
            </div>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1600px] px-4 sm:px-6 pt-0 sm:pt-6 pb-[20px] sm:pb-6 mx-auto">

            {/* Loading state */}
            {isLoadingTrackingData && (
              <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">{translateInterfaceText("Carregando rastreamentos...")}</p>
                </div>
              </div>
            )}

            {/* Content based on current step */}
            {!isLoadingTrackingData && currentStep === 'empty' && (
              <EmptyTrackingState onActivate={handleStartTracking} />
            )}

            {!isLoadingTrackingData && currentStep === 'selection' && (
              <PlatformSelection
                onConfirm={handlePlatformSelection}
                onBack={handleBackToEmpty}
              />
            )}

            {!isLoadingTrackingData && currentStep === 'dashboard' && (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                {/* Tabs Header - Movido para fora dos TabsContent */}
                <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
                  <TabsList className="bg-transparent border-b border-border rounded-none h-auto p-0 gap-0">
                    <TabsTrigger
                      value="cadastro"
                      className="rounded-tl-md rounded-tr-md rounded-b-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:bg-[#2563EB] data-[state=active]:text-white data-[state=inactive]:text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#374151] px-3 py-1.5 sm:px-6 sm:py-3 text-[14px] sm:text-sm font-semibold transition-all duration-150"
                    >
                      {translateInterfaceText("Cadastro")}</TabsTrigger>
                    <TabsTrigger
                      value="metricas"
                      className="rounded-tl-md rounded-tr-md rounded-b-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:bg-[#2563EB] data-[state=active]:text-white data-[state=inactive]:text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#374151] px-3 py-1.5 sm:px-6 sm:py-3 text-[14px] sm:text-sm font-semibold transition-all duration-150"
                    >
                      {translateInterfaceText("Métricas")}</TabsTrigger>
                  </TabsList>

                  {activeTab === 'metricas' ? (
                    <PeriodPicker
                      value={dateRange}
                      onChange={setDateRange}
                      align="end"
                      // No mobile o botão fica só com os ícones, como antes.
                      className="h-8 sm:h-9 w-auto min-h-0 px-3 sm:px-4 text-[13px] sm:text-sm rounded-lg [&>span]:hidden sm:[&>span]:inline"
                    />
                  ) : (
                    <Button
                      onClick={handleBackToSelection}
                      className="h-8 sm:h-9 px-2.5 sm:px-4 text-[13px] sm:text-sm font-semibold rounded-lg bg-[#2563EB] hover:bg-[#2563EB]/90 text-white"
                    >
                      <span className="hidden sm:inline">{translateInterfaceText("Adicionar Plataforma")}</span>
                      <span className="sm:hidden">{translateInterfaceText("Adicionar")}</span>
                    </Button>
                  )}
                </div>
                  
                <TabsContent value="cadastro" className="mt-0 w-full px-0">
                  <WebhooksTable
                    onTabChange={() => setActiveTab('metricas')}
                    savedWebhooks={savedWebhooks}
                    onWebhooksSaved={handleWebhooksSaved}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onBack={handleBackToSelection}
                  />
                </TabsContent>

                <TabsContent value="metricas" className="mt-0 w-full px-0">
                  <MetricsTablePremium
                    savedWebhooks={savedWebhooks}
                    onBack={handleBackToSelection}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    dateRange={dateRange}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
